<template>
  <div class="add-preference">
    <div v-if="errorMessage" role="alert">
      <div class="add-preference__error" data-message-type="error" v-html="errorMessage"></div>
    </div>

    <div v-if="product.addToCartAllowed && product.productAvailableAtContext">
      <button
        ref="button"
        @click.stop.prevent="showPreferenceList"
        :id="`add-preference-${product.id}`"
        class="add-preference__button btn"
        aria-haspopup="true"
        :aria-expanded="popoverVisible || modalVisible"
        v-bind:class="`add-preference__button--${size}`">
        <span v-if="totalQuantity">
          {{ totalQuantity }}
          <template v-if="product.pricedByWeight">
            {{ product.defaultSku.priceInfo.listPrice.unit }}
          </template>
          <template v-else-if="totalQuantity.toString().length < 3">
            added
          </template>
        </span>
        <template v-else>Add to {{ bucketDisplayName }}</template>
        <i class="fa fa-chevron-down add-preference__button-icon"/>
      </button>

      <!--
        Not using b-popover here as it was causing Vue reactivity issues
        i.e. preference changes were not being updated to the UI even though
        they were saved on the backend and Vuex was updated successfully
      -->
      <div
        v-if="popoverVisible"
        class="add-preference__popover"
        ref="popover">
        <preference-item
          v-for="preference in product.purchasePreferenceList.purchasePreferences"
          :key="`popover-${preference.preferenceId}`"
          :preference="preference"
          :product="product"
          :bucket="bucket"
          :totalQuantity="totalQuantity"
          :listname="listname"
          :buttonType="buttonType"
          v-on:update-error-message="updateErrorMessage"
          v-on:show-coupon-on-close="updateShowCouponOnClose">
        </preference-item>
      </div>

      <b-modal
        v-model="modalVisible"
        @hide="hideModal"
        @hidden="resetModal"
        :id="`add-preference-modal-${product.id}`"
        :modal-class="[
          'add-preference__modal',
          fadeModal ? 'add-preference__modal--fading' : ''
        ]"
        hide-header
        hide-footer
        no-fade>
        <div class="add-preference__modal-content">
          <div class="add-preference__modal-title">
            {{ product.purchasePreferenceList.label ? `Choose ${product.purchasePreferenceList.label}` : 'Choose preference' }}
          </div>
          <div v-if="errorMessage" role="alert">
            <div class="add-preference__error" data-message-type="error" v-html="errorMessage"></div>
          </div>
          <div class="add-preference__modal-list">
            <preference-item
              v-for="preference in product.purchasePreferenceList.purchasePreferences"
              :key="`modal-${preference.preferenceId}`"
              :preference="preference"
              :product="product"
              :bucket="bucket"
              :totalQuantity="totalQuantity"
              :listname="listname"
              :buttonType="buttonType"
              v-on:update-error-message="updateErrorMessage"
              v-on:show-coupon-on-close="updateShowCouponOnClose">
            </preference-item>
          </div>
          <button
            @click="hideModal"
            class="btn btn-primary btn-primary--blue add-preference__modal-button">
            Done
          </button>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import PreferenceItem from './PreferenceItem';

export default {
  name: 'PreferenceAddToBucket',
  props: {
    product: {
      type: Object
    },
    bucket: {
      type: String,
      default: 'cart' // cart, orderAddOns. Used for figuring out how many are in your bucket
    },
    size: {
      type: String,
      default: 'normal' // normal, large
    },
    listname: {
      type: String,
      default: ''
    },
    buttonType: {
      type: String,
      default:  'spa' // spa, nonspa
    },
    verticalPosition: {
      type: String,
      default: 'bottom' // top, bottom
    }
  },
  data() {
    return {
      popoverVisible: false,
      modalVisible: false,
      fadeModal: false,
      errorMessage: null,
      showCouponOnClose: false,
    };
  },
  components: {
    'preference-item': PreferenceItem,
  },
  computed: {
    totalQuantity() {
      if (typeof this.$store.state[this.bucket] === 'undefined') {
        return;
      }

      let commerceItems = [];

      if (this.bucket === 'cart') {
        this.$store.state.cart.commerceItems.forEach(
          category => {
            const items = category.items.filter(
              item => item.product.id === this.product.id
            );
            commerceItems = commerceItems.concat(items);
          }
        );
      } else if (this.bucket === 'orderAddOns') {
        commerceItems = this.$store.state.orderAddOns.orderAddOn.commerceItems.filter(
          item => item.product.id === this.product.id
        );
      }

      const total = commerceItems.reduce(
        (total, commerceItem) =>
          total + (this.product.pricedByWeight ? commerceItem.estimatedWeight : commerceItem.quantity)
      , 0);

      return total;
    },
    bucketDisplayName() {
      switch (this.bucket) {
        case 'orderAddOns':
          return 'order';
        default:
          return 'cart';
      }
    },
    isMobile() {
      return window.heb.screen.isSmall;
    },
  },
  methods: {
    showPreferenceList() {
      if (this.isMobile) {
        this.modalVisible = true;
      } else if (!this.popoverVisible) {
        this.showPopover();
      }
    },
    showPopover() {
      this.popoverVisible = true;
      this.$nextTick(() => {
        let x = 0;
        let y = 0;

        // If product card is within a carousel, position the popover within its bounds
        // Otherwise, position the popover within the window bounds
        const carouselParent = $(this.$refs.popover).closest('.slick-list, .jcarousel-list');
        const parentBoundaries =
          carouselParent && carouselParent.length ?
          carouselParent.get(0).getBoundingClientRect() :
          { right: window.innerWidth, bottom: window.innerHeight, top: 0 };
        const buttonBoundaries = this.$refs.button.getBoundingClientRect();
        const popoverBoundaries = this.$refs.popover.getBoundingClientRect();
        const margin = 12;

        // Check to see if popover would overflow the bounds if positioned right
        // If so, flip top left
        const shouldPositionLeft = buttonBoundaries.left + popoverBoundaries.width > parentBoundaries.right;
        if (shouldPositionLeft) {
          x = -popoverBoundaries.width + buttonBoundaries.width;
        }

        // Check to see if popover would overflow the bounds if positioned bottom,
        // or if it would not overflow the top bounds if positioned top
        // If so, flip to top
        const shouldPositionTop = this.verticalPosition === 'bottom' ?
          (buttonBoundaries.bottom + this.$refs.popover.offsetHeight > parentBoundaries.bottom) :
          (buttonBoundaries.top - this.$refs.popover.offsetHeight > parentBoundaries.top);

        if (shouldPositionTop) {
          const spaceTop = buttonBoundaries.top  - parentBoundaries.top - margin;
          if (this.$refs.popover.offsetHeight > spaceTop) {
            this.$refs.popover.style['max-height'] = `${spaceTop}px`;
          }

          y = -this.$refs.popover.getBoundingClientRect().height - buttonBoundaries.height - margin;
        }

        this.$refs.popover.style.transform = `translate(${x}px, ${y}px`;

        window.addEventListener('click', this.closePopoverOnClick, true);
        window.addEventListener('keyup', this.closePopoverOnEsc, true);
      });
    },
    closePopover() {
      this.popoverVisible = false;
      window.removeEventListener('click', this.closePopoverOnClick, true);
      window.removeEventListener('keyup', this.closePopoverOnEsc, true);
      this.showCoupon();
    },
    closePopoverOnEsc(event) {
      if (event.keyCode === 27 || event.key === 'Escape' || event.key === 'Esc') {
        this.closePopover();
      }
    },
    closePopoverOnClick(event) {
      if (!this.$refs.popover.contains(event.target)) {
        if (this.$refs.button.contains(event.target)) {
          event.stopPropagation();
          event.preventDefault();
        }
        this.closePopover();
      }
    },
    hideModal(event) {
      if (!this.modalVisible) {
        return;
      }

      if (event) {
        event.preventDefault();
      }

      this.fadeModal = true;

      setTimeout(() => {
        this.modalVisible = false;
        this.showCoupon();
      }, 300);
    },
    resetModal() {
      this.fadeModal = false;
    },
    updateErrorMessage(message) {
      this.errorMessage = message;
    },
    updateShowCouponOnClose() {
      this.showCouponOnClose = true;
    },
    showCoupon() {
      if (!this.showCouponOnClose) {
        return;
      }

      this.showCouponOnClose = false;

      // Find the JSP one-to-one coupon card and display it
      // Otherwise, show coupon one-to-one card, Vue version
      const couponCardInDom = document.getElementById(`${this.product.id}-${this.product.defaultSku.availableCouponIds[0]}`);
      if (couponCardInDom) {
        couponCardInDom.style.display = 'block';
      } else {
        this.$emit('show-coupon-one-to-one-card');
      }
    }
  },
  watch: {
    isMobile: {
      immediate: true,
      handler(newValue) {
        if (!newValue) {
          this.hideModal();
        }
      },
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../assets/lib";
@import "../../../../static-assets/css/_btn.scss";
@import "../../../../static-assets/css/_product-card.scss";

.add-preference__error {
  font-size: 14px;
  font-weight: 400;
  padding: 12px;
  display: flex;
  justify-content: center;
  word-break: break-word;
  color: #414142;
  background-color: #fafafa;
  border: 1px solid #eeeeee;
  border-radius: 4px;
  margin-bottom: 8px;
}

.add-preference__button {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 4px;
  margin: 0;
  width: 100%;
  height: 38px;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  background-color: $color-heb-primary;
  white-space: nowrap;
  padding: 8px;

  &:hover {
    background-color: darken($color-heb-primary, 10%);
  }

  @media screen and (min-width: $breakpoint-tablet) {
    &.add-preference__button--large {
      height: 52px;
      font-size: 16px;
    }
  }
}

.add-preference__button-icon {
  margin-left: 8px;
}

.add-preference__popover {
  position: absolute;
  background-color: #fff;
  width: 300px;
  max-height: 315px;
  overflow-y: auto;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  box-shadow: 0 1px 5px rgba(27,31,35,0.15);
  margin: 6px 0;
  z-index: 1060;
}
</style>

<style lang="scss">
@keyframes preferencesFadeIn {
  from { opacity: 0; }
  to { opacity: 0.5; }
}

@keyframes preferencesFadeOut {
  from { opacity: 0.5; }
  to { opacity: 0; }
}

@keyframes preferencesSlideIn {
  from { transform: translate3d(0, 100%, 0); }
  to { transform: translate3d(0, 0, 0); }
}

@keyframes preferencesSlideOut {
  from { transform: translate3d(0, 0, 0); }
  to { transform: translate3d(0, 100%, 0); }
}

.modal.add-preference__modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;

  + .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #000;
    opacity: 0.5;
  }

  &:not(.add-preference__modal--fading) {
   animation: preferencesSlideIn 300ms ease-out;

    + .modal-backdrop {
      animation: preferencesFadeIn 300ms ease-out;
    }
  }

  &.add-preference__modal--fading {
    transform: translate3d(0, 100%, 0);
    animation: preferencesSlideOut 300ms ease-out;

     + .modal-backdrop {
      opacity: 0;
      animation: preferencesFadeOut 300ms ease-out;
    }
  }

  .modal-dialog,
  .modal-body,
  .modal-content {
    margin: 0;
    padding: 0;
    border: 0;
    background: transparent;
    height: 100%;
    width: 100%;

    &:focus {
      outline: 0;
    }
  }

  .modal-dialog {
    background: #fafafa;
    border-radius: 12px 12px 0 0;
    position: absolute;
    top: 64px;
    left: 0;
    width: 100%;
    height: calc(100% - 64px);
  }

  .add-preference__modal-content {
    height: 100%;
    padding: 0 16px;
    display: flex;
    flex-direction: column;
  }

  .add-preference__modal-title {
    font-size: 18px;
    line-height: 1.5;
    font-weight: 700;
    padding: 32px 0;
    text-align: center;
  }

  .add-preference__modal-list {
    background-color: #fff;
    padding-bottom: 90px;
    border-radius: 12px 12px 0 0;
    flex-grow: 1;
    overflow: auto;
  }

  .add-preference__modal-button {
    position: absolute;
    left: 0;
    bottom: 16px;
    height: 42px;
    width: calc(100% - 32px);
    margin: 0 16px;
    border-radius: 21px;
    box-shadow: 0 1px 5px rgba(27,31,35,0.15);
    font-size: 16px;
    font-weight: 700;
  }
}
</style>